import { useCallback, useEffect, useState } from 'react'
import { publicAPI } from '../services/api'

export type AlertSeverity = 'info' | 'success' | 'warning' | 'error'

export interface Alert {
  id: number
  title: string
  content: string
  severity: AlertSeverity
  modified_time: string
}

interface DismissedAlert {
  id: number
  modified_time: string
}

export const DISMISSED_KEY = 'bp:dismissed-alerts'

function readDismissed(): DismissedAlert[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeDismissed(list: DismissedAlert[]) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(list))
  } catch {
    // Private mode / storage full — dismissal just does not survive a reload.
  }
}

/**
 * Active alerts for the global alert bar.
 *
 * The backend already filters out inactive, unpublished and expired alerts and
 * returns them ordered by priority, so the list is rendered as-is. Dismissals are
 * stored per device against `modified_time`, so an edited alert reappears for a
 * user who dismissed the earlier wording.
 */
export function useAlerts() {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [dismissed, setDismissed] = useState<DismissedAlert[]>(readDismissed)

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await publicAPI.getAnnouncements({ content_type: 'alert' })
      const data: Alert[] = Array.isArray(response.data?.data) ? response.data.data : []
      setAlerts(data)

      // Drop dismissals for alerts the backend no longer returns, so the list
      // cannot grow without bound. Only on success — an empty list from a failed
      // request must not clear real dismissals.
      setDismissed((prev) => {
        const liveIds = new Set(data.map((a) => a.id))
        const next = prev.filter((d) => liveIds.has(d.id))
        if (next.length !== prev.length) writeDismissed(next)
        return next.length !== prev.length ? next : prev
      })
    } catch {
      // The bar is supplementary — a failed fetch shows nothing and says nothing.
      setAlerts([])
    }
  }, [])

  useEffect(() => {
    fetchAlerts()
    window.addEventListener('focus', fetchAlerts)
    return () => window.removeEventListener('focus', fetchAlerts)
  }, [fetchAlerts])

  const dismiss = useCallback((alert: Alert) => {
    setDismissed((prev) => {
      const next = [
        ...prev.filter((d) => d.id !== alert.id),
        { id: alert.id, modified_time: alert.modified_time },
      ]
      writeDismissed(next)
      return next
    })
  }, [])

  const visibleAlerts = alerts.filter(
    (a) => !dismissed.some((d) => d.id === a.id && d.modified_time === a.modified_time),
  )

  return { alerts: visibleAlerts, dismiss }
}
